import { Box, Grid, Paper, Tab, Tabs } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import ContactList from "components/chat/ContactList";
import ConversationList from "components/chat/ConversationList";
import EmptyChatState from "components/chat/EmptyChatState";
import RemoveContactModal from "components/chat/RemoveContactModal";
import DashboardLayout from "components/layouts/DashboardLayout";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import useChatSocket from "hooks/useChatSocket";
import {
  useContacts,
  useConversations,
  useCreateConversation,
  useRemoveContact,
} from "hooks/useConversations";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { ContactDto, ConversationDto } from "libs/cellera-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";
import {
  ChatSubscribeMessage,
  NewConversationMessageReponse,
} from "types/socket";

import ChatWindow from "./ChatWindow";

const ChatPage: React.FC = () => {
  const { colors } = useCelleraTheme();
  const [selectedConversation, setSelectedConversation] =
    useState<ConversationDto>();
  const [selectedContact, setSelectedContact] = useState<ContactDto>();

  const [contactToDelete, setContactToDelete] = useState<string | null>(null);
  const { mutateAsync: removeContact, isPending: isRemovingContact } =
    useRemoveContact();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [search, setSearch] = useState<string>("");

  const [searchDebounced] = useDebouncedValue(search, 500);

  const { socket } = useChatSocket();
  const queryClient = useQueryClient();
  const { showError } = useToast();
  const { user } = useAuth();
  const {
    data: conversationsData,
    isLoading: isLoadingConversations,
    fetchNextPage: fetchNextPageConversations,
    hasNextPage: hasNextPageConversations,
    isFetchingNextPage: isFetchingNextPageConversations,
  } = useConversations(activeTab === 0);
  const {
    data: contactsData,
    fetchNextPage: fetchNextPageContacts,
    isLoading: isLoadingContacts,
    hasNextPage: hasNextPageContacts,
    isFetchingNextPage: isFetchingNextPageContacts,
  } = useContacts(activeTab === 1, searchDebounced);
  const { mutateAsync: createConversation } = useCreateConversation();

  const tabContentScrollRef = useRef<HTMLDivElement>(null);

  const conversations = useMemo(() => {
    if (!conversationsData) return [];
    return conversationsData.pages.flatMap((page) => page.conversations);
  }, [conversationsData]);

  const contacts = useMemo(() => {
    if (!contactsData) return [];
    return contactsData.pages.flatMap((page) => page.contacts);
  }, [contactsData]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSelectedContact(undefined);
    setSelectedConversation(undefined);
    setSearch("");
  };

  const handleSelectConversation = useCallback(
    (conversation: ConversationDto) => {
      setSelectedConversation(conversation);
      queryClient.invalidateQueries({
        queryKey: ["user-presence"],
      });
      queryClient.invalidateQueries({
        queryKey: ["conversations"],
      });
    },
    [queryClient]
  );

  const handleSelectContact = useCallback(
    async (contact: ContactDto) => {
      setSelectedContact(contact);
      queryClient.invalidateQueries({
        queryKey: ["user-presence"],
      });
      if (user?.uid) {
        try {
          const conversation = await createConversation({
            createConversationDto: {
              patientId: contact.contactId,
              providerId: user?.uid,
            },
          });
          setSelectedConversation(conversation.conversation);
        } catch (error: unknown) {
          showError(
            "Error",
            error instanceof Error ? error.message : "Unknown error"
          );
          console.log("Create conversation error:", error);
        }
      }
    },
    [queryClient, user?.uid, createConversation, showError]
  );

  const handleRemoveContact = useCallback(async () => {
    if (contactToDelete) {
      await removeContact(contactToDelete);
      setContactToDelete(null);
      queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });
    }
  }, [contactToDelete, removeContact, queryClient]);

  const handleLoadMoreConversations = useCallback(() => {
    if (hasNextPageConversations && !isFetchingNextPageConversations) {
      fetchNextPageConversations();
    }
  }, [
    fetchNextPageConversations,
    hasNextPageConversations,
    isFetchingNextPageConversations,
  ]);

  const handleLoadMoreContacts = useCallback(() => {
    if (hasNextPageContacts && !isFetchingNextPageContacts) {
      fetchNextPageContacts();
    }
  }, [fetchNextPageContacts, hasNextPageContacts, isFetchingNextPageContacts]);

  // Handle scroll event for load more
  useEffect(() => {
    const scrollContainer = tabContentScrollRef.current;
    if (!scrollContainer) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const nearBottom = scrollHeight - scrollTop - clientHeight < 100;
      if (nearBottom) {
        if (activeTab === 1) {
          handleLoadMoreContacts();
        } else {
          handleLoadMoreConversations();
        }
      }
    };
    // Add scroll event listener
    scrollContainer.addEventListener("scroll", handleScroll);
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [activeTab, handleLoadMoreConversations, handleLoadMoreContacts]);

  // listen for new conversation event
  useEffect(() => {
    if (!socket) return;

    const onNewConversation = (conversation: NewConversationMessageReponse) => {
      if (
        selectedConversation &&
        conversation.conversationId === selectedConversation?.id
      ) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["conversations"],
      });
    };

    socket.on(ChatSubscribeMessage.NEW_CONVERSATION_MESSAGE, onNewConversation);

    return () => {
      socket.off(
        ChatSubscribeMessage.NEW_CONVERSATION_MESSAGE,
        onNewConversation
      );
    };
  }, [socket, queryClient, selectedConversation]);

  return (
    <DashboardLayout title="Communications">
      <Paper
        sx={{
          border: `1px solid ${colors.border.light}`,
          borderRadius: 1,
          p: 2,
          height: "100%",
          maxHeight: "calc(100vh - 98px)",
          overflow: "hidden",
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            width: "100%",
            height: "100%",
          }}
          columns={{ xs: 4, sm: 8, md: 12 }}
        >
          <Grid size={{ xs: 6, md: 3 }} sx={{ height: "100%" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                backgroundColor: colors.background.paper,
                borderRight: `2px solid ${colors.border.light}`,
              }}
            >
              {/* Tabs Header */}
              <Box
                sx={{
                  borderBottom: `1px solid ${colors.border.light}`,
                  flexShrink: 0,
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  aria-label="chat tabs"
                  sx={{
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: colors.text.secondary,
                      minHeight: "48px",
                    },
                    "& .Mui-selected": {
                      color: colors.primary.main,
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: colors.primary.main,
                    },
                  }}
                >
                  <Tab label="Chat" />
                  <Tab label="Contacts" />
                </Tabs>
              </Box>

              {/* Tab Content */}
              <Box
                ref={tabContentScrollRef}
                sx={{
                  flex: 1,
                  overflow: "auto",
                  "&::-webkit-scrollbar": {
                    width: "0px",
                  },
                }}
              >
                {activeTab === 0 && (
                  <ConversationList
                    isLoading={isLoadingConversations}
                    conversations={conversations}
                    selectedConversationId={selectedConversation?.id}
                    onSelect={handleSelectConversation}
                    isLoadingMore={isFetchingNextPageConversations}
                  />
                )}
                {activeTab === 1 && (
                  <ContactList
                    contacts={contacts}
                    selectedContact={selectedContact}
                    onSelect={handleSelectContact}
                    isLoadingMore={isFetchingNextPageContacts}
                    isLoading={isLoadingContacts}
                    search={search}
                    onSearch={setSearch}
                    onOpenRemoveContactModal={setContactToDelete}
                  />
                )}
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 6, md: 9 }} sx={{ height: "100%" }}>
            {selectedConversation ? (
              <ChatWindow conversation={selectedConversation} />
            ) : (
              <EmptyChatState />
            )}
          </Grid>
        </Grid>
      </Paper>

      <RemoveContactModal
        open={!!contactToDelete}
        isRemoving={!!isRemovingContact}
        onClose={() => setContactToDelete(null)}
        onRemove={handleRemoveContact}
      />
    </DashboardLayout>
  );
};

export default ChatPage;
